/**
 * Offline identity: the signed-in user's profile + organization, saved on the
 * device so an app opened with NO SIGNAL can render its saved data instead of
 * "No workspace assigned" (offline plan step A,
 * docs/plans/2026-09-24-tech-offline-master-plan.md).
 *
 * OPT-IN. Nothing is saved or read unless an app registers a store — the
 * Technician PWA does (client-tech/src/main.tsx); the office, sales and time
 * apps do not, so AuthContext/OrganizationContext behave there exactly as
 * before.
 *
 * WHAT THIS IS NOT. It is a rendering aid, never an authority. The Firebase
 * credential still governs every request; the server re-derives identity,
 * role and org from the token on every call and never trusts these values.
 * The saved copy is used ONLY when a read fails for lack of CONNECTIVITY — a
 * server refusal (permission-denied: a deactivated or removed account) never
 * falls back to it — and only for the SAME uid that saved it. It is wiped with
 * the rest of the offline cache on sign-out and on a user switch.
 */
import type { User, Organization, OrganizationMember } from '../../types/firestore';
import { isDeviceOffline } from '../offlineErrors';
import { isDegradedReadError } from '../firestore/earnedRead';

export interface OfflineIdentity {
  readonly user: User;
  readonly organization?: Organization;
  readonly membership?: OrganizationMember | null;
}

export interface OfflineIdentityStore {
  load(uid: string): Promise<OfflineIdentity | null>;
  /** Merge `patch` into the saved identity for `uid`. */
  save(uid: string, patch: Partial<OfflineIdentity>): Promise<void>;
  clear(): Promise<void>;
}

let store: OfflineIdentityStore | null = null;

/** Called once at app start by an app that wants offline cold starts. `null` unregisters. */
export function registerOfflineIdentityStore(next: OfflineIdentityStore | null): void {
  store = next;
}

export function getOfflineIdentityStore(): OfflineIdentityStore | null {
  return store;
}

/**
 * Codes that mean "could not reach the server" — as opposed to the server
 * answering no. Firestore reports an offline read as `unavailable`; Firebase
 * Auth reports a token refresh with no network as `auth/network-request-failed`.
 */
const CONNECTIVITY_CODES: ReadonlySet<string> = new Set([
  'unavailable',
  'firestore/unavailable',
  'deadline-exceeded',
  'auth/network-request-failed',
]);

/**
 * True only for a failure caused by missing connectivity. A server refusal
 * (permission-denied, unauthenticated, not-found, user-disabled…) is NEVER a
 * connectivity failure, even while the device is offline — the saved identity
 * must not paper over an account the server has turned away.
 */
export function isConnectivityFailure(error: unknown): boolean {
  // A read that only the local cache could answer (earnedRead.ts) — no server reached.
  if (isDegradedReadError(error)) return true;
  const code = String((error as { code?: unknown } | null | undefined)?.code ?? '');
  if (CONNECTIVITY_CODES.has(code)) return true;
  if (code) return false; // a coded answer from the server is not a connectivity failure
  return isDeviceOffline();
}

/** Load the saved identity for `uid`, or null — never throws (a broken store must not break sign-in). */
export async function loadSavedIdentity(uid: string): Promise<OfflineIdentity | null> {
  if (!store) return null;
  try {
    const saved = await store.load(uid);
    // Belt and braces: the record must describe the uid it is filed under.
    return saved && saved.user?.userId === uid ? saved : null;
  } catch {
    return null;
  }
}

/** Save part of the identity for `uid` — best effort, never throws. */
export function saveIdentity(uid: string, patch: Partial<OfflineIdentity>): void {
  if (!store) return;
  void store.save(uid, patch).catch(() => undefined);
}
